// ENGINE_RULES §1 — the numeric policy, in one place. Public module (ARCH §8.1): apps, `src/metrics`,
// `src/periods` and the engine all import it; it imports nothing.
//
// N-0: half away from zero, once, at display, from unrounded inputs. JavaScript has doubles, C# has
// `decimal`; these helpers make both give the same answer at the traps (1.005, 0.125, 30.375, ±2.5) and
// never return NaN, infinity or −0 (N-5).

/**
 * N-1 / API §1.4: how many decimals a DERIVED figure is recorded at on the wire — `Money` "up to 6 dp",
 * `Pct` "up to 6 dp". The contract's own number, named once (T-372: `shares.ts` was emitting a `Pct` at 13
 * decimals, which is where an association order of 1e−14 reached the wire and the two servers disagreed).
 * Exact sums and user-set money are the currency's minor units instead — `sumMoney` below.
 */
export const DERIVED_DECIMALS = 6;

/** Shift the decimal point of `v` by `n` places through the exponent of its string form — the double nearest
 *  to the decimal `v × 10^n`, so `shiftExponent(1.39, -2)` is the double `Intl` prints as `0.0139` and
 *  `shiftExponent(12.5, 3)` is exactly `12500` (a multiplication in doubles is not: `1.39 / 100` carries noise).
 *  Exact for every double: `String(5e-7)` is `"5e-7"`, so the exponent is ADDED to, never appended to
 *  (`"5e-7e6"` → NaN — QA's `met-round-exponent-form`, T-067; the same fix as `tools/check-demo-story.mjs`).
 *  Public since T-015: `src/format` scales percentages and compact figures with it. */
export function shiftExponent(v: number, n: number): number {
  const [mantissa, exponent = '0'] = String(v).split('e');
  return Number(`${mantissa}e${Number(exponent) + n}`);
}

/**
 * N-0 `roundHalfAway(x, dp)`: round `x` to `dp` decimal places, ties away from zero.
 * `1.005 → 1.01`, `0.125 → 0.13`, `30.375 → 30.38`, `2.5 → 3`, `−2.5 → −3`, `−0.004 → 0` (never −0).
 * `toFixed` and `Math.round(x * 10^dp)` both fail on `1.005`; this strips the binary noise first
 * (15 significant digits — ample for money and percentages, N-10) and shifts through the exponent.
 * A non-finite input is returned as is so a caller's own guard (N-5) can name the problem.
 */
export function roundHalfAway(x: number, dp = 0): number {
  if (!Number.isFinite(x)) return x;
  if (!Number.isInteger(dp) || dp < 0)
    throw new RangeError(`roundHalfAway: dp must be a non-negative integer, got ${dp}`);
  const magnitude = Number(Math.abs(x).toPrecision(15));
  const rounded = Math.sign(x) * shiftExponent(Math.round(shiftExponent(magnitude, dp)), -dp);
  return rounded === 0 ? 0 : rounded; // −0 → 0
}

/**
 * The canonical decimal string of `x` at exactly `dp` decimals — what the golden vectors, the wire and the
 * parity crawl compare (`"18.042000"`, `"-17.200000"`, `"0.00"`): ASCII minus, no exponent, never `"-0.00"`.
 * Rounds with `roundHalfAway` first; `toFixed` is safe on a value that already has at most `dp` decimals.
 */
export function toDecimalString(x: number, dp: number): string {
  const rounded = roundHalfAway(x, dp);
  if (!Number.isFinite(rounded))
    throw new RangeError(`toDecimalString: ${x} is not a finite number`);
  return rounded.toFixed(dp);
}

/** The number a decimal string denotes (`"1092.00"` → 1092). Throws on anything that is not a plain decimal. */
export function parseDecimal(text: string): number {
  if (!/^-?\d+(\.\d+)?$/.test(text))
    throw new RangeError(`parseDecimal: "${text}" is not a decimal string`);
  return Number(text);
}

/** How many decimals a decimal string shows (`"18.042000"` → 6, `"7500"` → 0). */
export function decimalsOf(text: string): number {
  const dot = text.indexOf('.');
  return dot < 0 ? 0 : text.length - dot - 1;
}

/**
 * N-5 division: `numerator ÷ denominator`, or `null` when the denominator is 0, `null` or not finite, or the
 * numerator is `null`. Never NaN, never infinity. The quotient is unrounded (N-2, N-10): callers round once,
 * at the point where a value leaves for the wire or the screen.
 */
export function divide(numerator: number | null, denominator: number | null): number | null {
  if (numerator === null || denominator === null) return null;
  if (!Number.isFinite(numerator) || !Number.isFinite(denominator) || denominator === 0)
    return null;
  return numerator / denominator;
}

/** N-3: `part ÷ whole × 100` as a percent number (`1.39` means 1.39 %), or `null` per `divide`. */
export function percent(part: number | null, whole: number | null): number | null {
  const q = divide(part, whole);
  return q === null ? null : q * 100;
}

/** N-1: a sum of money normalised to the currency's minor units (2 for USD, 0 for JPY, 3 for KWD). The
 *  value does not change — inputs have at most `minorUnits` decimals — only the binary noise of adding
 *  them goes (`0.1 + 0.2 + 0.3` is `0.6000000000000001` in doubles). */
export function sumMoney(amounts: readonly number[], minorUnits: number): number {
  let total = 0;
  for (const amount of amounts) total += amount;
  return roundHalfAway(total, minorUnits);
}

/** N-8: the arithmetic mean, unrounded (a mean inside a calculation is never rounded — N-10). `null` for no values. */
export function mean(values: readonly number[]): number | null {
  if (values.length === 0) return null;
  let total = 0;
  for (const v of values) total += v;
  return total / values.length;
}

/**
 * N-7: the `p`-th percentile (0 … 100) by inclusive linear interpolation — sort ascending, `rank = p ÷ 100 ×
 * (n − 1)`, `value = x[floor(rank)] + frac(rank) × (x[ceil(rank)] − x[floor(rank)])`. Unrounded (N-10).
 * `null` for no values. ENGINE §7.2.1: the 14 daily totals of Sep 8–21 give `p10` 142.6 (rank 1.3) and `p90`
 * 156.5 (rank 11.7). Added by T-018 for the forecast range.
 */
export function percentile(values: readonly number[], p: number): number | null {
  if (values.length === 0) return null;
  if (!Number.isFinite(p) || p < 0 || p > 100)
    throw new RangeError(`percentile: p must be within 0 … 100, got ${p}`);
  const sorted = [...values].sort((a, b) => a - b);
  const rank = (p / 100) * (sorted.length - 1);
  const lo = Math.floor(rank);
  const hi = Math.ceil(rank);
  const below = sorted[lo]!;
  const above = sorted[hi]!;
  return below + (rank - lo) * (above - below);
}
